import clsx from 'clsx';

import { Icon } from 'common/icon';
import { Header } from 'components/slider/header';

import { ISlot } from 'constants/types';

interface SliderRowProps {
  btn?: boolean;
  name?: string;
  data?: ISlot[];
  count?: string;
  title?: string;
  hasIcon?: boolean;
  guides?: string[];
  guidesHref?: string;
  guidesLabel?: string;
  hasFooter?: boolean;
  description?: string;
  type?: 'mini' | 'medium';
  hasFavoritesActions?: boolean;
  container?: 'mini' | 'medium';
  anchor?: string;
  isWidthStretching?: boolean;
}

import { Loader } from 'common';
import { BlurHashImage } from 'components/blurhash-image';
import { LogoOverlay, LogoOverlayWrapper } from 'components/logo-overlay';
import { Routes } from 'constants/routes';
import { useFavorites } from 'hooks/useFavorites';
import Link from 'next/link';
import { getItemIdentifier } from 'utils/slug';
import styles from './styles.module.css';
import { GuideItem } from 'components/guide-item';
import guideStyles from 'components/guide-item/styles.module.css';

export const Card = ({
  data,
  hasIcon,
  hasFooter,
  hasFavoritesActions,
  isWidthStretching,
}: {
  data: ISlot | null;
  hasIcon?: boolean;
  hasFooter?: boolean;
  hasFavoritesActions?: boolean;
  isWidthStretching?: boolean;
}) => {
  const { removeFromFavorite, isLoading } = useFavorites();

  if (!data) {
    return null;
  }

  return (
    <div className={clsx(styles.cards__card, styles.slot_card)}>
      <Link
        prefetch={false}
        key={data._id}
        href={Routes.slotPage(getItemIdentifier(data))}
      >
        <div
          className={clsx(styles.slot_card__wrapper, {
            [styles.widthStretching]: isWidthStretching,
          })}
        >
          <div className={styles.slot_card__cover}>
            <LogoOverlayWrapper>
              <BlurHashImage
                alt={data?.name + ' logo'}
                blurhash={data?.blurHash}
                src={data?.imageUrl ?? ''}
                sizes="(max-width: 769px) 160px, 180px"
              />

              <LogoOverlay alt={data?.name} image={data?.logoUrl} />
            </LogoOverlayWrapper>
          </div>

          {!hasFooter && (
            <div className={styles.slot_card__name}>{data?.name}</div>
          )}
        </div>

        {hasFavoritesActions && (
          <div className={styles.favorite_action_shadow} />
        )}
      </Link>
      {hasFavoritesActions && (
        <div
          onClick={(e) => {
            e.stopPropagation();
            removeFromFavorite({ favoriteId: data?._id });
          }}
          className={styles.wrapper_favorite_action}
        >
          {isLoading ? <Loader size="medium" /> : <Icon.Remove />}
        </div>
      )}
    </div>
  );
};

export const CardsMini = ({
  data,
  name,
  count,
  guides,
  guidesHref,
  guidesLabel = 'View all guides',
  hasIcon,
  hasFooter,
  title = '',
  description,
  hasFavoritesActions,
  container = 'medium',
  anchor,
  isWidthStretching,
}: SliderRowProps) => {
  const containerStyles = clsx({
    [styles.container]: container === 'medium',
    [styles.container_s]: container === 'mini',
  });

  return (
    <section className={styles.cards} id={anchor}>
      <div className={containerStyles}>
        {title && (
          <div className={styles.cards_header_container}>
            {title && <h1 className={styles.cards__title}>{title}</h1>}
            {description && (
              <h3 className={styles.cards__description}>{description}</h3>
            )}
            <div className={styles.cards_guides_container}>
              {guidesHref && (
                <Link href={guidesHref} className={guideStyles.container}>
                  <Icon.Guide />
                  <span className={guideStyles.text}>{guidesLabel}</span>
                  <Icon.Arrow />
                </Link>
              )}
              {guides?.map((id) => <GuideItem key={id} id={id} />)}
            </div>
          </div>
        )}
        <Header name={name} count={count} url={Routes.allSlots} />
        <div
          className={clsx(styles.cards__row, {
            [styles.widthStretching]: isWidthStretching,
          })}
        >
          {data?.map((item) => (
            <Card
              data={item}
              key={item?._id}
              hasIcon={hasIcon}
              hasFooter={hasFooter}
              hasFavoritesActions={hasFavoritesActions}
              isWidthStretching={isWidthStretching}
            />
          ))}
        </div>
      </div>
    </section>
  );
};
